import { z } from 'zod'
import { calendarDaySchema, instantSchema, wallClockSchema } from '../validators/temporal.validator.js'
import { floorObjectSchema } from './floor-object.dto.js'
import { zoneSchema } from './zone.dto.js'
import { staticLabelSchema } from './static-label.dto.js'
import { furnitureSourceSchema } from '../enums/furniture-source.enum.js'

/**
 * What an unauthenticated crew sees. See `docs/public-schedule.md`.
 *
 * These shapes are the privacy boundary, and they are written as a **closed
 * list of what is public** rather than an existing DTO with fields removed.
 * Omission is the safer direction: a field added to `EventDto` tomorrow appears
 * on a public page if this reuses it, and nobody reviewing that change would be
 * looking here.
 *
 * So: no event name, no clients, no contacts, no documents, no guest count, no
 * timeline.
 */

/** One event on the venue's list — enough to tell two apart, and no more. */
export const publicEventSummaryDtoSchema = z.object({
  id: z.string().uuid(),
  date: calendarDaySchema,
  /** Wall clock at the venue, as everywhere else. Null only if unset. */
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  /** The spaces in use, in the order they are used. Identifies the event when
   * two share a day, which a venue running concurrent spaces often has. */
  spaces: z.array(z.string()),
  /**
   * The day has already happened — this one is here to be cleared, not set.
   *
   * The list reaches a day back so a crew arriving the morning after still has
   * the page, and a past date shown without saying so reads as something coming
   * up. Decided by the server at the venue's clock rather than by the browser,
   * which may be a zone away.
   *
   * `.default(false)` so a new web build reading an older API mid-deploy does
   * not fail to parse the whole list.
   */
  isPast: z.boolean().default(false),
})

export const publicScheduleDtoSchema = z.object({
  venueName: z.string(),
  /** How many days **ahead** this list runs, so the page can say so. The day it
   * also reaches back is carried by each event's `isPast`, not by this. */
  windowDays: z.number().int().positive(),
  events: z.array(publicEventSummaryDtoSchema),
})

export type PublicScheduleDto = z.infer<typeof publicScheduleDtoSchema>

/** One piece to set out, and whose it is. */
export const publicSetPieceDtoSchema = z.object({
  name: z.string(),
  count: z.number().int().positive(),
  /**
   * Where it comes from. A crew that cannot tell the venue's own stock from a
   * client's van will either set out furniture that has not arrived or leave
   * somebody's chairs in it.
   */
  source: furnitureSourceSchema,
})

export type PublicSetPieceDto = z.infer<typeof publicSetPieceDtoSchema>

/** One kind of table in a space, set with the same number of chairs. */
export const publicTableCountDtoSchema = z.object({
  /** The table's catalog name, as the set list names it. */
  name: z.string(),
  /** Whose tables these are, as the set list says: a rental company sets its own. */
  source: furnitureSourceSchema,
  /** How many tables of this name and source have this many chairs. */
  tables: z.number().int().positive(),
  /** The chairs at each; 0 for a table with none drawn round it (a buffet, a cake table). */
  chairsEach: z.number().int().nonnegative(),
})

export type PublicTableCountDto = z.infer<typeof publicTableCountDtoSchema>

/**
 * A space's tables and the chairs at them, counted from its plan
 * (docs/vendor-growth.spec.md, "What vendors on an event see"): what a caterer
 * sets places for. Names and counts only, like the set list.
 */
export const publicSeatingDtoSchema = z.object({
  /** By name, then whose (the venue's first), then most chairs first. */
  tables: z.array(publicTableCountDtoSchema),
  /** Chairs near no table: ceremony rows, lounge seating. */
  chairsNotAtTable: z.number().int().nonnegative(),
})

export type PublicSeatingDto = z.infer<typeof publicSeatingDtoSchema>

/** One space, as the crew needs it: when, what it looks like, what goes in it. */
export const publicPhaseDtoSchema = z.object({
  id: z.string().uuid(),
  spaceName: z.string(),
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  /** The room's drawing: its size, its fixed features, and the furniture on it. */
  width: z.number(),
  height: z.number(),
  zones: z.array(zoneSchema),
  staticLabels: z.array(staticLabelSchema),
  objects: z.array(floorObjectSchema),
  setList: z.array(publicSetPieceDtoSchema),
  /** Null only from an API older than the counts, mid-deploy. */
  seating: publicSeatingDtoSchema.nullable().default(null),
  /** Markdown, written by whoever plans the event. Rendered as text and lists. */
  instructions: z.string().nullable(),
})

export type PublicPhaseDto = z.infer<typeof publicPhaseDtoSchema>

export const publicEventDetailDtoSchema = z.object({
  venueName: z.string(),
  date: calendarDaySchema,
  startsAt: wallClockSchema.nullable(),
  endsAt: wallClockSchema.nullable(),
  phases: z.array(publicPhaseDtoSchema),
})

export type PublicEventDetailDto = z.infer<typeof publicEventDetailDtoSchema>

/**
 * A venue's share link, as its manager sees it.
 *
 * `url` carries the token and is returned every time rather than once: this is
 * a permalink somebody bookmarks, not a credential to be copied down. Only the
 * venue's own managers can read it, and a link nobody can re-read is a link that
 * gets replaced every time somebody loses the message — which breaks every crew
 * bookmark. That is why the token is stored in clear; see the Prisma model.
 */
export const venueShareLinkDtoSchema = z.object({
  id: z.string().uuid(),
  /** Relative, so the same row serves localhost, staging and production. */
  url: z.string(),
  /**
   * Real instants, branded like every other timestamp that crosses this
   * boundary — `ApiTokenDto`, which this shape is modeled on, brands the same
   * two fields. Bare `z.string()` accepted "banana" at the boundary and left the
   * screen to guard against a date it could not parse.
   */
  lastUsedAt: instantSchema.nullable(),
  createdAt: instantSchema,
})

/** A venue has one live link, or none until somebody makes it. */
export const venueShareLinkStateDtoSchema = z.object({
  link: venueShareLinkDtoSchema.nullable(),
})

export type VenueShareLinkStateDto = z.infer<typeof venueShareLinkStateDtoSchema>
